import { inject, Injectable, OnDestroy, signal } from '@angular/core';
import { MenuService } from 'app';
import { AuthService } from 'auth';
import { catchError, filter, interval, of, startWith, Subscription, switchMap } from 'rxjs';
import { Logger } from 'shared';
import { HEALTH_TOOL_GROUPS, HealthToolGroup, SupportTicket, SupportTicketService, TicketPulse } from './support-ticket.service';

/**
 * Sidebar badge for the two Health Tools queues: "support wrote something you
 * have not opened yet".
 *
 * Zammad keeps no per-customer read state, so the comparison is made here — the
 * backend reports each ticket's last agent contact and the last opened
 * timestamp is remembered per user in localStorage. Consequence worth knowing:
 * the badge is per browser, so reading a reply on the phone leaves it marked on
 * the desktop until it is opened there too.
 */
@Injectable({ providedIn: 'root' })
export class SupportUnreadService implements OnDestroy {
  private logger = new Logger('SupportUnreadService');
  private support = inject(SupportTicketService);
  private menuService = inject(MenuService);
  private authService = inject(AuthService);

  /** Menu url fragment per Zammad group — must match the sidebar entries. */
  private static readonly MENU_PATHS: Record<HealthToolGroup, string> = {
    // Since 2026-10-02 "Ask Dr. Future" in the sidebar is the AI companion
    // (/dr-future); the human Health Concierge is reached through the support hub,
    // so its replies badge "Help & Support" too (counts are summed per path).
    [HEALTH_TOOL_GROUPS.concierge]: '/support',
    [HEALTH_TOOL_GROUPS.kristy]: '/ask-dr-kristy',
    // General support replies badge the "Help & Support" entry.
    [HEALTH_TOOL_GROUPS.support]: '/support'
  };

  private static readonly POLL_INTERVAL_MS = 60000; // 1 minute

  /** Unread count per Zammad group, for anything that wants to show it in-page. */
  readonly counts = signal<Record<string, number>>({});

  private pollingSubscription: Subscription | null = null;
  private userId: number | null = null;

  initialize(): void {
    this.authService.getUser$()
      .pipe(filter(user => !!user))
      .subscribe(user => {
        this.userId = user?.id ?? null;
        this.startPolling();
      });

    this.authService.getUser$()
      .pipe(filter(user => !user))
      .subscribe(() => {
        this.stopPolling();
        this.clearBadges();
        this.userId = null;
      });
  }

  startPolling(): void {
    if (this.pollingSubscription) {
      return;
    }

    this.pollingSubscription = interval(SupportUnreadService.POLL_INTERVAL_MS)
      .pipe(
        startWith(0),
        switchMap(() => this.support.summary().pipe(
          catchError(err => {
            this.logger.error('Failed to fetch support summary:', err);
            return of({ groups: {} });
          })
        ))
      )
      .subscribe(response => this.applySummary(response.groups ?? {}));
  }

  stopPolling(): void {
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = null;
  }

  refresh(): void {
    this.support.summary()
      .pipe(catchError(() => of({ groups: {} })))
      .subscribe(response => this.applySummary(response.groups ?? {}));
  }

  /** Called when a ticket thread is opened, so its latest reply stops counting. */
  markSeen(ticket: SupportTicket | null | undefined): void {
    if (!ticket?.id) {
      return;
    }
    const seen = this.readSeen();
    // Fall back to updated_at so opening a ticket whose reply Zammad has not
    // stamped as agent contact still clears it.
    seen[ticket.id] = ticket.last_agent_contact_at || ticket.updated_at || new Date().toISOString();
    this.writeSeen(seen);
    this.refresh();
  }

  private applySummary(groups: Record<string, TicketPulse[]>): void {
    const seen = this.readSeen();
    const counts: Record<string, number> = {};

    for (const [group, tickets] of Object.entries(groups)) {
      counts[group] = (tickets ?? []).filter(ticket => {
        if (!ticket.last_agent_contact_at) {
          return false;
        }
        const seenAt = seen[ticket.id];
        return !seenAt || new Date(ticket.last_agent_contact_at) > new Date(seenAt);
      }).length;
    }

    this.counts.set(counts);

    const perPath = new Map<string, number>();
    for (const [group, path] of Object.entries(SupportUnreadService.MENU_PATHS)) {
      perPath.set(path, (perPath.get(path) ?? 0) + (counts[group] ?? 0));
    }
    for (const [path, count] of perPath) {
      this.menuService.updateMenuBadge(
        path,
        count > 0 ? { color: 'danger', text: count > 99 ? '99+' : count.toString() } : undefined
      );
    }
  }

  private clearBadges(): void {
    this.counts.set({});
    for (const path of new Set(Object.values(SupportUnreadService.MENU_PATHS))) {
      this.menuService.updateMenuBadge(path, undefined);
    }
  }

  // The key is user-scoped: two members using the same browser must not inherit
  // each other's read state.
  private get storageKey(): string {
    return `gh-support-seen:${this.userId ?? 'anonymous'}`;
  }

  private readSeen(): Record<number, string> {
    try {
      const raw = localStorage.getItem(this.storageKey);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }

  private writeSeen(seen: Record<number, string>): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(seen));
    } catch (err) {
      this.logger.warn('Could not persist support read state', err);
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }
}
